/**
 * Observability bootstrap — Sentry (error tracking) + PostHog (product analytics).
 *
 * Both are gated on their respective env vars: empty values are no-ops, so
 * the same code runs in preview, staging, and production without conditionals
 * littering the rest of the app.
 *
 * Sentry: captures uncaught exceptions, network failures, React render errors.
 * PostHog: captures pageviews + custom funnel events for conversion analysis.
 */

import * as Sentry from "@sentry/react";
import posthog from "posthog-js";

let _initialised = false;
let _posthogLoaded = false;
let _phKey = "";
let _phHost = "";

const CONSENT_KEY = "worthify_cookie_consent";

/** Debug-level logger for intentionally non-fatal failures (analytics must
 * never crash the app, but silent swallowing makes debugging impossible). */
const logSwallowed = (context, err) => {
  try { console.debug(`[observability] ${context}:`, err); } catch { /* console unavailable */ }
};

/**
 * Returns the current analytics consent state from localStorage.
 *   "analytics-accepted" → user opted in, PostHog should capture
 *   "essential-only"     → user rejected analytics, no capture
 *   null                 → user hasn't decided yet (banner still showing)
 */
function getConsent() {
  try {
    const v = localStorage.getItem(CONSENT_KEY) || "";
    if (v.startsWith("analytics-accepted")) return "analytics-accepted";
    if (v.startsWith("essential-only")) return "essential-only";
    return null;
  } catch (e) {
    logSwallowed("getConsent localStorage read failed", e);
    return null;
  }
}

/**
 * GDPR-safe lazy PostHog initialiser. Loads + initialises the SDK ONLY when
 * the user has explicitly accepted analytics. Until that point we don't:
 *   • fetch the PostHog `array.js` bundle
 *   • call `/decide` for feature flags / surveys
 *   • write any PostHog-managed localStorage entries
 *   • register any cookies or network requests against eu.i.posthog.com
 */
function _initPosthogIfConsented() {
  if (_posthogLoaded || !_phKey) return;
  if (getConsent() !== "analytics-accepted") return;
  posthog.init(_phKey, {
    api_host: _phHost,
    capture_pageview: true,
    capture_pageleave: true,
    disable_session_recording: true,
    autocapture: true,
    persistence: "localStorage",
    property_blacklist: ["$ip"],
    respect_dnt: true,
  });
  _posthogLoaded = true;
  try { window.posthog = posthog; } catch (e) { logSwallowed("window.posthog assignment", e); }
}

export function initObservability() {
  if (_initialised) return;
  _initialised = true;

  const sentryDsn = process.env.REACT_APP_SENTRY_DSN || "";
  if (sentryDsn) {
    Sentry.init({
      dsn: sentryDsn,
      environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "preview",
      // Keep PII off — we operate in a payment context.
      sendDefaultPii: false,
      tracesSampleRate: 0.1,
      // Session Replay — 5 % normal, 100 % on error.
      replaysSessionSampleRate: 0.05,
      replaysOnErrorSampleRate: 1.0,
      beforeSend(event, hint) {
        const error = hint.originalException;
        if (
          error &&
          (error.status === 404 ||
            (error.response && error.response.status === 404) ||
            error.message?.includes("404"))
        ) {
          return null;
        }
        return event;
      },
      integrations: [
        Sentry.replayIntegration({
          maskAllText: true,
          maskAllInputs: true,
          blockAllMedia: true,
          networkDetailAllowUrls: [],
        }),
      ],
    });
  }

  _phKey = process.env.REACT_APP_POSTHOG_KEY || "";
  _phHost = process.env.REACT_APP_POSTHOG_HOST || "https://eu.i.posthog.com";
  // PECR-compliant: don't even touch the PostHog SDK until consent. The
  // helper below is a no-op when consent !== "analytics-accepted".
  _initPosthogIfConsented();
}

/**
 * Called by CookieBanner when the user clicks "Accept analytics".
 * Persists the choice and lazy-loads PostHog (first-time visitors) or
 * re-enables capture (returning visitors).
 */
export function acceptAnalyticsConsent() {
  try { localStorage.setItem(CONSENT_KEY, "analytics-accepted:" + Date.now()); } catch (e) { logSwallowed("consent persist failed", e); }
  _initPosthogIfConsented();
  if (_posthogLoaded) {
    try { posthog.opt_in_capturing(); } catch (e) { logSwallowed("posthog opt-in", e); }
    try { posthog.capture("analytics_consent_granted"); } catch (e) { logSwallowed("posthog capture", e); }
  }
}

/**
 * Called by CookieBanner when the user clicks "Essential only".
 * Persists the choice, opts out, and proactively clears any PostHog
 * localStorage entries that may have leaked in from a previous SDK build.
 */
export function rejectAnalyticsConsent() {
  try { localStorage.setItem(CONSENT_KEY, "essential-only:" + Date.now()); } catch (e) { logSwallowed("consent persist failed", e); }
  if (_posthogLoaded) {
    try { posthog.opt_out_capturing(); } catch (e) { logSwallowed("posthog opt-out", e); }
    try { posthog.reset(true); } catch (e) { logSwallowed("posthog reset", e); }
  }
  // Belt-and-braces: wipe any persisted PostHog identifiers under PECR.
  try {
    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith("ph_") || k.startsWith("__posthog__") || k.includes("posthog")) {
        localStorage.removeItem(k);
      }
    });
  } catch (e) { logSwallowed("posthog localStorage wipe", e); }
}

/**
 * Track a discrete funnel event. Used heavily across the analysis + payment
 * flows so we can later compute drop-off rates and £1→£4.99 conversion.
 *
 * Example event names (snake_case, past tense):
 *   - landing_viewed
 *   - analysis_started
 *   - analysis_completed
 *   - verdict_screen_viewed
 *   - paywall_consent_modal_opened
 *   - paywall_consent_accepted
 *   - stripe_redirect_initiated
 *   - payment_completed
 *   - payment_failed
 *   - dashboard_viewed
 *   - premium_tile_clicked
 *   - pdf_download_started
 *   - vin_added_post_analysis
 */
export function trackEvent(name, props) {
  if (!_posthogLoaded) return;
  try {
    posthog.capture(name, props || {});
  } catch (e) {
    // never crash the app over analytics — but leave a trace for debugging
    logSwallowed(`trackEvent(${name})`, e);
  }
}

/**
 * Optional: identify the visitor by a stable anonymous ID (we don't have
 * accounts). Useful for cross-session funnel reports.
 */
export function setVisitorProperty(key, value) {
  if (!_posthogLoaded) return;
  try {
    posthog.people.set({ [key]: value });
  } catch (e) { logSwallowed("setVisitorProperty", e); }
}

export { Sentry, posthog };
